/**
 * ElecBid — Load Calculator Workspace
 *
 * Wraps the CECLoadCalc with:
 *   - Toolbar (Past Calcs / Save / PDF / Push to Jobber)
 *   - Save panel (link to client + Jobber job)
 *   - Past Calcs drawer (search by client / job number)
 *   - PDF generation via printable popup window
 *
 * The calculator itself stays unchanged — we just listen to its
 * onStateChange snapshot and feed it a key+initialState when reloading.
 */

import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../lib/apiFetch";
import CECLoadCalc from "./CECLoadCalc";
import { usePermissions } from "../hooks/usePermissions";

const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", border: "#2a303c",
  lime: "#a5ce3a", limeDim: "rgba(165,206,58,0.12)",
  green: "#22c55e", red: "#ef4444", amber: "#f59e0b",
  text: "#f0f4f8", muted: "#8892a4", dim: "#4a5568",
  blue: "#3b82f6", blueDim: "rgba(59,130,246,0.12)",
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
function escapeHtml(str = "") {
  return String(str)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-CA", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return ""; }
}

// Build a self-contained HTML string for the PDF popup. Mirrors the on-screen
// breakdown so what the client sees matches what we see.
function buildPrintHtml({ snapshot, meta }) {
  const { inputs, result, serviceAmps } = snapshot;
  const headroom = Math.round((serviceAmps - result.demandAmps) * 10) / 10;
  const overCapacity = result.demandAmps > serviceAmps;

  const generatedAt = new Date().toLocaleString("en-CA", {
    year: "numeric", month: "long", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });

  const rows = [];
  rows.push({ label: `Base load (${result.totalM2.toFixed(1)} m²)`, value: `${result.baseLoad.toLocaleString()} W`,
    sub: "Rule 8-200(1)(a)(i)–(ii): 5,000 W first 90 m² + 1,000 W per additional 90 m²" });

  if (result.heatingOrAC > 0 && result.heatAcMode === "both") {
    rows.push({
      label: "Electric heating (demand factored)",
      value: `${Math.round(result.heatingDemand).toLocaleString()} W`,
      sub: `Rule 8-200(1)(a)(iii): ${result.heatingWatts.toLocaleString()} W → 100% first 10 kW + 75% remainder`,
    });
    rows.push({
      label: "Air conditioning",
      value: `${result.acDemand.toLocaleString()} W`,
      sub: "Rule 8-200(3): heat &amp; A/C can run simultaneously — both added at 100%",
    });
  } else if (result.heatingOrAC > 0) {
    rows.push({
      label: result.usingHeating ? "Electric heating (demand factored)" : "Air conditioning",
      value: `${result.heatingOrAC.toLocaleString()} W`,
      sub: result.usingHeating
        ? `Rule 8-200(1)(a)(iii): ${result.heatingWatts.toLocaleString()} W → 100% first 10 kW + 75% remainder`
        : `Rule 8-200(1)(a)(iii): AC at 100% nameplate${result.heatingDemand > 0 ? " (larger than heating)" : ""}`,
    });
  }

  if (result.rangeEnabled && !result.rangeShed) {
    rows.push({
      label: "Electric range",
      value: `${result.rangeCalculated.toLocaleString()} W`,
      sub: `Rule 8-200(1)(a)(iv): ${(result.rangeNameplate / 1000).toFixed(1)} kW nameplate → ${
        result.rangeNameplate <= 12000
          ? "6,000 W"
          : `6,000 + 40% of ${((result.rangeNameplate - 12000) / 1000).toFixed(1)} kW excess`
      }`,
    });
  }

  if (result.waterHeatingTotal > 0) {
    const items = (inputs.waterHeatingItems || [])
      .filter(i => i.enabled && !i.shed)
      .map(i => `${escapeHtml(i.label)} (${(i.watts || 0).toLocaleString()} W)`)
      .join(", ");
    rows.push({
      label: "Water heating / hot tubs / pool",
      value: `${result.waterHeatingTotal.toLocaleString()} W`,
      sub: `Rule 8-200(1)(a)(v): 100% nameplate · ${items}`,
    });
  }

  if (result.evDemand > 0 || (inputs.evEnabled && inputs.evems && (inputs.evWatts || 0) > 0)) {
    rows.push({
      label: `EV charging${inputs.evems ? " (EVEMS — shed)" : ""}`,
      value: `${result.evDemand.toLocaleString()} W`,
      sub: inputs.evems
        ? `Rule 8-200(1)(a)(vi): ${(inputs.evWatts || 0).toLocaleString()} W nameplate fully shed by EVEMS`
        : "Rule 8-200(1)(a)(vi): 100% nameplate",
    });
  }

  if (result.additionalDemand > 0) {
    const items = (inputs.additionalItems || [])
      .filter(i => !i.shed)
      .map(i => `${escapeHtml(i.label)} (${(i.watts || 0).toLocaleString()} W)`)
      .join(", ");
    rows.push({
      label: "Additional loads (>1,500 W)",
      value: `${result.additionalDemand.toLocaleString()} W`,
      sub: `${
        result.additionalRuleBranch === "with_range"
          ? `Rule 8-200(1)(a)(vii): range present, ${result.additionalRawTotal.toLocaleString()} W × 25%`
          : `Rule 8-200(1)(a)(vii): no range, first ${result.additionalFirstBlock.toLocaleString()} W at 100% + ${result.additionalRemainder.toLocaleString()} W × 25%`
      } · ${items}`,
    });
  }

  const newLoadDemand = result.newLoadDemand ?? result.newLoadWatts;
  const newLoadLabel = result.newLoadLabel || inputs.newLoadType;
  if (newLoadDemand > 0) {
    rows.push({
      label: `New load${newLoadLabel ? ` (${newLoadLabel})` : ""}`,
      value: `${newLoadDemand.toLocaleString()} W`,
      sub: "Added at 100% nameplate to test if it fits the existing service.",
    });
  }

  // Loads the calc relies on an energy management system to drop.
  const shedLoads = result.shedLoads || [];
  const shedHtml = shedLoads.length ? `
  <div class="section-title">EMS-Shed Loads — excluded from demand</div>
  <table>
    <tbody>
      ${shedLoads.map(s => `
      <tr>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#111827;">${escapeHtml(s.label)}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;text-align:right;font-family:'Courier New',monospace;color:#6b7280;white-space:nowrap;">${(s.watts || 0).toLocaleString()} W nameplate → 0 W</td>
      </tr>`).join("")}
    </tbody>
  </table>
  <div style="font-size:11px;color:#6b7280;margin-top:6px;">
    These loads are controlled by an energy management system that fully sheds them, so they are counted at 0 W.
    Total shed: ${(result.shedWattsTotal || 0).toLocaleString()} W nameplate.
  </div>` : "";

  const statusColor = overCapacity ? "#b91c1c" : (result.demandAmps / serviceAmps > 0.8 ? "#b45309" : "#15803d");
  const statusLabel = overCapacity
    ? `OVER CAPACITY by ${Math.abs(headroom)} A`
    : `${headroom} A headroom remaining`;

  const breakdownHtml = rows.map(r => `
    <tr>
      <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;">
        <div style="font-weight:600;color:#111827;">${escapeHtml(r.label)}</div>
        <div style="font-size:11px;color:#6b7280;margin-top:2px;">${r.sub}</div>
      </td>
      <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:right;font-family:'Courier New',monospace;font-weight:600;color:#111827;white-space:nowrap;">
        ${escapeHtml(r.value)}
      </td>
    </tr>`).join("");

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"/>
<title>Load Calculation${meta.client_name ? ` — ${escapeHtml(meta.client_name)}` : ""}</title>
<style>
  @page { size: letter; margin: 0.6in; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #111827; margin: 0; padding: 24px; background: #fff; }
  h1, h2 { margin: 0; }
  .header { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:14px; border-bottom: 3px solid ${C.lime}; }
  .brand { font-size: 22px; font-weight: 800; letter-spacing: 0.02em; color: #111827; }
  .brand-sub { font-size: 11px; color: #6b7280; margin-top: 2px; }
  .doc-title { font-size: 13px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 700; }
  .doc-date { font-size: 11px; color: #6b7280; margin-top: 2px; }
  .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px; padding: 14px 0; border-bottom: 1px solid #e5e7eb; font-size: 12px; }
  .meta-label { color: #6b7280; }
  .meta-value { color: #111827; font-weight: 600; }
  .summary-box { margin: 18px 0; padding: 14px 16px; border: 2px solid ${statusColor}; border-radius: 8px; background: ${statusColor}10; }
  .summary-headline { font-size: 22px; font-weight: 800; color: ${statusColor}; }
  .summary-sub { font-size: 12px; color: #374151; margin-top: 4px; }
  .demand-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin: 14px 0; }
  .demand-stat { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px; text-align: center; }
  .demand-stat-label { font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; }
  .demand-stat-value { font-size: 18px; font-weight: 700; color: #111827; margin-top: 4px; font-family: 'Courier New', monospace; }
  .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #6b7280; margin: 18px 0 6px; }
  table { width: 100%; border-collapse: collapse; }
  .total-row td { padding: 10px; border-top: 2px solid #111827; font-size: 14px; font-weight: 700; }
  .footer { margin-top: 26px; padding-top: 14px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #6b7280; line-height: 1.6; }
  .signature { margin-top: 24px; padding-top: 14px; border-top: 1px solid #e5e7eb; display: grid; grid-template-columns: 1fr 1fr; gap: 30px; font-size: 11px; color: #6b7280; }
  .sig-line { border-bottom: 1px solid #111827; height: 30px; margin-bottom: 4px; }
  @media print { body { padding: 0; } .no-print { display: none; } }
  .print-bar { position: fixed; top: 8px; right: 8px; z-index: 99; }
  .print-bar button { padding: 8px 14px; border: 1px solid #d1d5db; border-radius: 6px; background: #fff; cursor: pointer; font-size: 13px; margin-left: 6px; }
  .print-bar button.primary { background: ${C.lime}; color: #111827; border-color: ${C.lime}; font-weight: 700; }
</style>
</head>
<body>
  <div class="print-bar no-print">
    <button onclick="window.print()" class="primary">Save as PDF / Print</button>
    <button onclick="window.close()">Close</button>
  </div>

  <div class="header">
    <div>
      <div class="brand">Browning Electric</div>
      <div class="brand-sub">CEC 2024 Residential Load Calculation</div>
    </div>
    <div style="text-align:right;">
      <div class="doc-title">Load Calculation</div>
      <div class="doc-date">${escapeHtml(generatedAt)}</div>
    </div>
  </div>

  <div class="meta-grid">
    <div><span class="meta-label">Client: </span><span class="meta-value">${escapeHtml(meta.client_name || "—")}</span></div>
    <div><span class="meta-label">Job #: </span><span class="meta-value">${escapeHtml(meta.job_number || (meta.jobber_request_id ? "(pending — request)" : "—"))}</span></div>
    <div><span class="meta-label">Address: </span><span class="meta-value">${escapeHtml(meta.job_address || "—")}</span></div>
    <div><span class="meta-label">Project: </span><span class="meta-value">${escapeHtml(meta.job_title || meta.request_title || "—")}</span></div>
    <div><span class="meta-label">Service: </span><span class="meta-value">${serviceAmps} A @ 240 V</span></div>
    <div><span class="meta-label">Above-grade area: </span><span class="meta-value">${(parseFloat(inputs.aboveGradeSqFt) || 0).toLocaleString()} sq ft</span></div>
    <div><span class="meta-label">Basement: </span><span class="meta-value">${(parseFloat(inputs.basementSqFt) || 0).toLocaleString()} sq ft (× 75%)</span></div>
    <div><span class="meta-label">Total area: </span><span class="meta-value">${result.totalM2.toFixed(1)} m²</span></div>
  </div>

  <div class="summary-box">
    <div class="summary-headline">${overCapacity ? "⚠ " : "✓ "}${result.demandAmps} A on ${serviceAmps} A service</div>
    <div class="summary-sub">${escapeHtml(statusLabel)}</div>
  </div>

  <div class="demand-stats">
    <div class="demand-stat">
      <div class="demand-stat-label">Calculated Demand</div>
      <div class="demand-stat-value">${result.totalDemand.toLocaleString()} W</div>
    </div>
    <div class="demand-stat">
      <div class="demand-stat-label">Demand Amps</div>
      <div class="demand-stat-value">${result.demandAmps} A</div>
    </div>
    <div class="demand-stat">
      <div class="demand-stat-label">Service Capacity</div>
      <div class="demand-stat-value">${serviceAmps} A</div>
    </div>
  </div>

  <div class="section-title">Demand Breakdown — CEC 2024 Rule 8-200</div>
  <table>
    <tbody>
      ${breakdownHtml}
      <tr class="total-row">
        <td>Total Calculated Demand</td>
        <td style="text-align:right;font-family:'Courier New',monospace;">${result.totalDemand.toLocaleString()} W</td>
      </tr>
      <tr class="total-row">
        <td>Calculated Amps (÷ 240 V)</td>
        <td style="text-align:right;font-family:'Courier New',monospace;">${result.demandAmps} A</td>
      </tr>
    </tbody>
  </table>
  ${shedHtml}

  ${meta.notes ? `
    <div class="section-title">Notes</div>
    <div style="font-size:12px;color:#374151;line-height:1.6;white-space:pre-wrap;">${escapeHtml(meta.notes)}</div>
  ` : ""}

  <div class="signature">
    <div>
      <div class="sig-line"></div>
      <div>Prepared by</div>
    </div>
    <div>
      <div class="sig-line"></div>
      <div>Date</div>
    </div>
  </div>

  <div class="footer">
    Calculated per Canadian Electrical Code 2024 (26th Edition), Rule 8-200 — Basic demand for single-dwelling residential services.
    Prepared by Browning Electric. This calculation is based on inputs provided and does not constitute an electrical inspection.
  </div>

  <script>
    // Auto-trigger print dialog on load. User can cancel and use the buttons.
    window.addEventListener('load', function() { setTimeout(function() { window.print(); }, 300); });
  </script>
</body></html>`;
}

// Build the plain-text load-calc note pushed to a Jobber job or request.
// Shared by the "Push to Jobber Notes" button and the auto-push on Save so
// both land an identical, full breakdown on the record.
function buildJobberNoteMessage(snapshot, meta) {
  const { inputs = {}, result, serviceAmps } = snapshot;
  const headroom = Math.round((serviceAmps - result.demandAmps) * 10) / 10;
  const overCap = result.demandAmps > serviceAmps;
  const w = (n) => `${Math.round(n || 0).toLocaleString()}W`;
  const itemLines = (items) => items.map(i => `  - ${i.label || "Load"}: ${w(i.watts)}`);
  const lines = [
    `CEC 2024 Load Calculation — ${new Date().toLocaleDateString("en-CA")}`,
    ``,
    `Service: ${serviceAmps}A @ 240V`,
    `Calculated demand: ${w(result.totalDemand)} (${result.demandAmps}A)`,
    overCap ? `OVER CAPACITY by ${Math.abs(headroom)}A` : `Headroom: ${headroom}A`,
    ``,
    `LOADS COUNTED`,
    `Base load (${result.totalM2.toFixed(1)}m²): ${w(result.baseLoad)}`,
  ];
  if (result.heatingOrAC > 0 && result.heatAcMode === "both") {
    lines.push(`Heating: ${w(result.heatingWatts)} nameplate → ${w(result.heatingDemand)}`);
    lines.push(`AC: ${w(result.acDemand)} (runs concurrent w/ heating)`);
  } else if (result.heatingOrAC > 0 && result.usingHeating) {
    lines.push(`Heating: ${w(result.heatingWatts)} nameplate → ${w(result.heatingOrAC)}${result.acDemand > 0 ? ` (larger than ${w(result.acDemand)} AC)` : ""}`);
  } else if (result.heatingOrAC > 0) {
    lines.push(`AC: ${w(result.heatingOrAC)}${result.heatingDemand > 0 ? ` (larger than ${w(result.heatingDemand)} heating)` : ""}`);
  }
  if (result.rangeEnabled && !result.rangeShed) {
    lines.push(`Range: ${w(result.rangeNameplate)} nameplate → ${w(result.rangeCalculated)}`);
  }
  if (result.waterHeatingTotal > 0) {
    lines.push(`Water heating/spa (100%): ${w(result.waterHeatingTotal)}`);
    lines.push(...itemLines((inputs.waterHeatingItems || []).filter(i => i.enabled && !i.shed)));
  }
  if (result.evDemand > 0) lines.push(`EV charger: ${w(result.evDemand)}`);
  if (result.additionalDemand > 0) {
    lines.push(`Additional loads: ${w(result.additionalRawTotal)} nameplate → ${w(result.additionalDemand)}`);
    lines.push(...itemLines((inputs.additionalItems || []).filter(i => !i.shed)));
  }
  const newLoadDemand = result.newLoadDemand ?? result.newLoadWatts;
  const newLoadLabel = result.newLoadLabel || inputs.newLoadType;
  if (newLoadDemand > 0) lines.push(`New load${newLoadLabel ? ` (${newLoadLabel})` : ""}: ${w(newLoadDemand)}`);

  // Every load the calc relies on an EMS to drop, with its nameplate, so the
  // record shows exactly what must stay under energy management.
  const shedLoads = result.shedLoads || [];
  if (shedLoads.length) {
    lines.push(``);
    lines.push(`EMS-SHED LOADS (energy management fully sheds these — counted at 0W)`);
    lines.push(...itemLines(shedLoads));
    lines.push(`Total shed: ${w(result.shedWattsTotal)} nameplate`);
  }
  if (meta.notes) { lines.push(``); lines.push(`Notes: ${meta.notes}`); }
  lines.push(``);
  lines.push(`Generated by ElecBid · CEC 2024 Rule 8-200`);
  return lines.join("\n");
}

// The calc's request note is created once and edited in place after that
// (load_calculations.jobber_request_note_id, which api/jobber.js
// pushRequestNote reads and records itself — the browser never names a note).
// The stored id belongs to the request the row was saved against, so it only
// carries over while that request is unchanged. Re-linking to another request
// (or unlinking) clears it in the SAME write that changes the request, and the
// next note is created on the new request, so the old request's note is never
// edited from here again. Pure: scripts/test_load_calc_request_note.js runs it.
function requestNotePlan(savedCalc, meta) {
  const storedId = (savedCalc && savedCalc.jobber_request_note_id) || null;
  const sameRequest = !!savedCalc
    && (savedCalc.jobber_request_id || "") === ((meta && meta.jobber_request_id) || "");
  return {
    noteId: sameRequest ? storedId : null,
    clearStored: !sameRequest && !!storedId,
  };
}

// The row the main save returns predates the note push, and pushRequestNote
// records a NEW note's id on the row after it (before the migration there's no
// column at all), so the copy kept in memory would lose the id on the very next
// save. Every path carries the id this save knows about — the live note, else
// the row's own, else the planned one when the push didn't answer — so the
// copy matches the row and a later re-link sends its clear (requestNotePlan;
// /api/load-calc also clears on a re-link by itself).
function carryNoteId(row, plan, liveNoteId) {
  if (!row) return row;
  const known = liveNoteId || row.jobber_request_note_id || (plan && plan.noteId) || null;
  return known && known !== row.jobber_request_note_id ? { ...row, jobber_request_note_id: known } : row;
}

// After a save, file the calc summary on the linked request's Jobber Notes
// timeline: ONE note per calc, created on the first save and edited in place on
// every save after (it rolls down onto the job, so a new note per save stacked
// copies in the crew's feed). The server picks the note from THIS calc's row
// (calcId) and records a new one there itself. Best-effort: a note failure
// never blocks the save. Returns the row to keep and the outcome to show.
// apiFetch and the message are passed in, so the test runs it as-is.
async function pushCalcRequestNote(apiFetchFn, { calc, meta, notePlan, buildMessage }) {
  if (!meta.jobber_request_id) return { calc, noteOutcome: null };
  try {
    const noteRes = await apiFetchFn("/api/jobber", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "pushRequestNote",
        requestId: meta.jobber_request_id,
        message: buildMessage(),
        calcId: (calc && calc.id) || undefined,
      }),
    });
    const noteData = await noteRes.json();
    const liveNoteId = noteData?.ok ? (noteData.note?.id || null) : null;
    return {
      calc: carryNoteId(calc, notePlan, liveNoteId),
      noteOutcome: liveNoteId
        ? { ok: true, edited: !!noteData.edited }
        : { ok: false, error: noteData?.error || "Note not saved" },
    };
  } catch (noteErr) {
    return { calc: carryNoteId(calc, notePlan, null), noteOutcome: { ok: false, error: noteErr.message } };
  }
}

// ─── Save Panel ────────────────────────────────────────────────────────────────
function SavePanel({ snapshot, savedCalc, meta, setMeta, onSaved, onClose, onPushedToJobber }) {
  const [saving, setSaving] = useState(false);
  const [pushingToJobber, setPushingToJobber] = useState(false);
  // "request" lets you link to a Jobber request (carries through to the job
  // when it converts). "job" links straight to an existing job.
  const [linkMode, setLinkMode] = useState("request");
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [error, setError] = useState("");
  const [pushResult, setPushResult] = useState(null);
  const { crewMember } = usePermissions() || {};

  const runSearch = async () => {
    if (!searchTerm.trim()) return;
    setSearching(true);
    setSearchResults(null);
    setError("");
    try {
      const action = linkMode === "request" ? "searchRequests" : "searchJobs";
      const res = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, searchTerm: searchTerm.trim() }),
      });
      const data = await res.json();
      if (!data.ok && data.error) {
        setError(`Jobber search failed: ${data.error}`);
        setSearchResults([]);
        return;
      }
      setSearchResults(linkMode === "request" ? (data.requests || []) : (data.jobs || []));
    } catch (err) {
      setError(`Jobber search failed: ${err.message}`);
    } finally {
      setSearching(false);
    }
  };

  const linkJob = (job) => {
    setMeta({
      ...meta,
      jobber_job_id: job.id,
      job_number: String(job.jobNumber || ""),
      job_title: job.title || "",
      client_name: job.client?.name || meta.client_name,
      jobber_client_id: job.client?.id || meta.jobber_client_id,
    });
    setSearchResults(null);
    setSearchTerm("");
  };

  const linkRequest = (request) => {
    // If the request already has a job spawned from it, pull that job in too —
    // skips the carry-through round-trip later.
    const firstJob = (request.jobs?.nodes || [])[0] || null;
    const propAddr = request.property?.address;
    const addrLine = propAddr
      ? [propAddr.street1, propAddr.city, propAddr.province].filter(Boolean).join(", ")
      : "";
    setMeta({
      ...meta,
      jobber_request_id: request.id,
      request_title: request.title || "",
      client_name: request.client?.name || meta.client_name,
      jobber_client_id: request.client?.id || meta.jobber_client_id,
      job_address: addrLine || meta.job_address,
      jobber_job_id: firstJob?.id || meta.jobber_job_id,
      job_number: firstJob ? String(firstJob.jobNumber || "") : meta.job_number,
      job_title: firstJob?.title || meta.job_title,
    });
    setSearchResults(null);
    setSearchTerm("");
  };

  const unlinkJob = () => {
    setMeta({ ...meta, jobber_job_id: "", job_number: "", job_title: "" });
  };

  const unlinkRequest = () => {
    // Unlinking the request also clears the carry-through job ref so we
    // don't keep a stale link without its parent.
    setMeta({
      ...meta,
      jobber_request_id: "", request_title: "",
      jobber_job_id: "", job_number: "", job_title: "",
    });
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { inputs, result, serviceAmps } = snapshot;
      const headroom = Math.round((serviceAmps - result.demandAmps) * 10) / 10;
      const notePlan = requestNotePlan(savedCalc, meta);
      const payload = {
        action: "save",
        id: savedCalc?.id || undefined,
        client_name: meta.client_name || null,
        jobber_client_id: meta.jobber_client_id || null,
        job_number: meta.job_number || null,
        jobber_job_id: meta.jobber_job_id || null,
        job_title: meta.job_title || null,
        job_address: meta.job_address || null,
        jobber_request_id: meta.jobber_request_id || null,
        request_title: meta.request_title || null,
        service_size_amps: serviceAmps,
        total_demand_w: result.totalDemand,
        demand_amps: result.demandAmps,
        headroom_amps: headroom,
        over_capacity: result.demandAmps > serviceAmps,
        inputs,
        result,
        notes: meta.notes || null,
        created_by_email: crewMember?.email || null,
        // Sent only to CLEAR a note id that belongs to a request this calc is
        // no longer linked to. An ordinary save leaves the key out, so it never
        // touches the stored id.
        ...(notePlan.clearStored ? { jobber_request_note_id: null } : {}),
      };
      const res = await apiFetch("/api/load-calc", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Save failed");

      // If this calc is linked to a Jobber request, file (or update) its note
      // there — see pushCalcRequestNote.
      const { calc, noteOutcome } = await pushCalcRequestNote(apiFetch, {
        calc: data.calc, meta, notePlan,
        buildMessage: () => buildJobberNoteMessage(snapshot, meta),
      });

      onSaved(calc, noteOutcome);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const pushToJobber = async () => {
    if (!meta.jobber_job_id) {
      setError("Link a Jobber job first.");
      return;
    }
    setPushingToJobber(true);
    setError("");
    setPushResult(null);
    try {
      const res = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "pushJobNote",
          jobId: meta.jobber_job_id,
          message: buildJobberNoteMessage(snapshot, meta),
        }),
      });
      const data = await res.json();
      if (!data.ok) {
        setPushResult({ ok: false, error: data.error || "Failed" });
        return;
      }
      setPushResult({ ok: true, noteId: data.note?.id });
      if (onPushedToJobber) onPushedToJobber(data.note?.id);
    } catch (err) {
      setPushResult({ ok: false, error: err.message });
    } finally {
      setPushingToJobber(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
    background: C.surface, color: C.text, fontSize: 13, fontFamily: "inherit",
    boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 11, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, display: "block" };

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
      padding: 16, marginBottom: 12,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
          {savedCalc ? "Update Saved Calculation" : "Save Load Calculation"}
        </div>
        <button onClick={onClose} style={{
          background: "none", border: "none", color: C.muted, cursor: "pointer",
          fontSize: 18, padding: 4,
        }}>×</button>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Client Name</label>
        <input
          style={inputStyle}
          value={meta.client_name || ""}
          onChange={(e) => setMeta({ ...meta, client_name: e.target.value })}
          placeholder="e.g. Smith Residence"
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Job Address</label>
        <input
          style={inputStyle}
          value={meta.job_address || ""}
          onChange={(e) => setMeta({ ...meta, job_address: e.target.value })}
          placeholder="123 Main St, Kelowna BC"
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Link to Jobber (optional)</label>

        {/* Currently linked: stack request + (optional) job. Unlink each
            independently — unlinking the request also clears the carry-through
            job so we don't leave it dangling. */}
        {(meta.jobber_request_id || meta.jobber_job_id) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 6 }}>
            {meta.jobber_request_id && (
              <div style={{
                display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                background: C.blueDim, border: `1px solid ${C.blue}`, borderRadius: 6,
              }}>
                <div style={{ flex: 1, fontSize: 13, color: C.text }}>
                  <span style={{
                    fontSize: 9, padding: "1px 5px", borderRadius: 3,
                    background: C.blue, color: "#fff", fontWeight: 800, marginRight: 6,
                  }}>REQUEST</span>
                  {meta.request_title || "Linked"}
                  {!meta.jobber_job_id && (
                    <span style={{ fontSize: 11, color: C.muted, marginLeft: 6 }}>
                      · job will auto-link on conversion
                    </span>
                  )}
                </div>
                <button onClick={unlinkRequest} style={{
                  background: "none", border: "none", color: C.muted, cursor: "pointer",
                  fontSize: 12, textDecoration: "underline",
                }}>Unlink</button>
              </div>
            )}
            {meta.jobber_job_id && (
              <div style={{
                display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                background: C.limeDim, border: `1px solid ${C.lime}`, borderRadius: 6,
              }}>
                <div style={{ flex: 1, fontSize: 13, color: C.text }}>
                  <span style={{
                    fontSize: 9, padding: "1px 5px", borderRadius: 3,
                    background: C.lime, color: "#000", fontWeight: 800, marginRight: 6,
                  }}>JOB</span>
                  <strong>#{meta.job_number}</strong> — {meta.job_title || "Linked"}
                </div>
                {/* Don't allow unlinking a job that came from a request — the
                    user should unlink the request instead. */}
                {!meta.jobber_request_id && (
                  <button onClick={unlinkJob} style={{
                    background: "none", border: "none", color: C.muted, cursor: "pointer",
                    fontSize: 12, textDecoration: "underline",
                  }}>Unlink</button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Search controls only visible when nothing is linked yet. */}
        {!(meta.jobber_request_id || meta.jobber_job_id) && (
          <>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              {[
                { key: "request", label: "Request" },
                { key: "job", label: "Job" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => { setLinkMode(opt.key); setSearchResults(null); }}
                  style={{
                    padding: "6px 12px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                    border: `1px solid ${linkMode === opt.key ? C.lime : C.border}`,
                    background: linkMode === opt.key ? C.limeDim : C.surface,
                    color: linkMode === opt.key ? C.lime : C.muted,
                    cursor: "pointer",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                placeholder={linkMode === "request"
                  ? "Search requests by client, title, address"
                  : "Search jobs by # or client name"}
              />
              <button onClick={runSearch} disabled={searching || !searchTerm.trim()} style={{
                padding: "0 14px", borderRadius: 6, border: "none",
                background: searching ? C.dim : C.blue, color: "#fff",
                fontSize: 12, fontWeight: 700, cursor: searching ? "default" : "pointer",
              }}>
                {searching ? "..." : "Search"}
              </button>
            </div>
            {searchResults && searchResults.length === 0 && (
              <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>No matches.</div>
            )}
            {searchResults && searchResults.length > 0 && (
              <div style={{ marginTop: 6, maxHeight: 220, overflowY: "auto",
                border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface }}>
                {searchResults.map((row) => {
                  if (linkMode === "request") {
                    const jobCount = row.jobs?.nodes?.length || 0;
                    return (
                      <button key={row.id} onClick={() => linkRequest(row)} style={{
                        display: "block", width: "100%", textAlign: "left", padding: "8px 10px",
                        background: "none", border: "none", borderBottom: `1px solid ${C.border}`,
                        color: C.text, cursor: "pointer", fontSize: 12,
                      }}>
                        <div style={{ fontWeight: 600 }}>
                          {row.client?.name || "Unknown"}
                          {row.title ? <span style={{ fontWeight: 400 }}> — {row.title}</span> : null}
                        </div>
                        <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>
                          {row.requestStatus || "—"}
                          {row.source ? ` · ${row.source}` : ""}
                          {jobCount > 0 ? ` · already converted to ${jobCount} job${jobCount === 1 ? "" : "s"}` : ""}
                        </div>
                      </button>
                    );
                  }
                  return (
                    <button key={row.id} onClick={() => linkJob(row)} style={{
                      display: "block", width: "100%", textAlign: "left", padding: "8px 10px",
                      background: "none", border: "none", borderBottom: `1px solid ${C.border}`,
                      color: C.text, cursor: "pointer", fontSize: 12,
                    }}>
                      <div style={{ fontWeight: 600 }}>#{row.jobNumber} — {row.client?.name || "Unknown"}</div>
                      <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>
                        {row.title || "Untitled"} · {row.jobStatus}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Notes (optional)</label>
        <textarea
          style={{ ...inputStyle, minHeight: 60, resize: "vertical", fontFamily: "inherit" }}
          value={meta.notes || ""}
          onChange={(e) => setMeta({ ...meta, notes: e.target.value })}
          placeholder="Anything worth recording with this calc"
        />
        {meta.jobber_request_id && (
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
            On save, this load calc is filed as one note on the request in Jobber. Later saves update that same note (it keeps its original spot in the feed).
          </div>
        )}
      </div>

      {error && (
        <div style={{ fontSize: 12, color: C.red, marginBottom: 10 }}>⚠ {error}</div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={save} disabled={saving} style={{
          flex: 1, minWidth: 120, padding: "10px 16px", borderRadius: 8, border: "none",
          background: saving ? C.dim : C.lime, color: "#000",
          fontWeight: 800, fontSize: 13, cursor: saving ? "default" : "pointer",
        }}>
          {saving ? "Saving…" : (savedCalc ? "Update" : "Save Calculation")}
        </button>
        {meta.jobber_job_id && (
          <button onClick={pushToJobber} disabled={pushingToJobber} style={{
            padding: "10px 16px", borderRadius: 8, border: "none",
            background: pushingToJobber ? C.dim : C.blue, color: "#fff",
            fontWeight: 700, fontSize: 13, cursor: pushingToJobber ? "default" : "pointer",
          }}>
            {pushingToJobber ? "Pushing…" : "Push to Jobber Notes"}
          </button>
        )}
      </div>

      {pushResult && (
        <div style={{
          marginTop: 10, padding: 10, borderRadius: 6, fontSize: 12,
          background: pushResult.ok ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
          color: pushResult.ok ? C.green : C.red,
        }}>
          {pushResult.ok
            ? "✓ Note added to Jobber job."
            : `Couldn't push to Jobber: ${pushResult.error}`}
        </div>
      )}
    </div>
  );
}

// ─── Past Calcs Drawer ─────────────────────────────────────────────────────────
function PastCalcsDrawer({ onLoad, onClose, currentId }) {
  const [calcs, setCalcs] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async (term = "") => {
    setLoading(true);
    try {
      const qs = term ? `&search=${encodeURIComponent(term)}` : "";
      const res = await apiFetch(`/api/load-calc?action=list${qs}`);
      const data = await res.json();
      setCalcs(data.calcs || []);
    } catch {
      setCalcs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    load(search);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this saved calculation?")) return;
    setDeletingId(id);
    try {
      await apiFetch("/api/load-calc", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete", id }),
      });
      setCalcs((prev) => (prev || []).filter((c) => c.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
      padding: 16, marginBottom: 12,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Past Calculations</div>
        <button onClick={onClose} style={{
          background: "none", border: "none", color: C.muted, cursor: "pointer",
          fontSize: 18, padding: 4,
        }}>×</button>
      </div>

      <form onSubmit={handleSearch} style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <input
          style={{
            flex: 1, padding: "8px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
            background: C.surface, color: C.text, fontSize: 13,
          }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by client name, job #, or title"
        />
        <button type="submit" style={{
          padding: "0 14px", borderRadius: 6, border: "none",
          background: C.lime, color: "#000", fontSize: 12, fontWeight: 700, cursor: "pointer",
        }}>Search</button>
      </form>

      {loading && <div style={{ fontSize: 12, color: C.muted }}>Loading…</div>}

      {!loading && calcs && calcs.length === 0 && (
        <div style={{ fontSize: 13, color: C.muted, padding: 12, textAlign: "center" }}>
          No saved calculations yet.
        </div>
      )}

      {!loading && calcs && calcs.length > 0 && (
        <div style={{ maxHeight: 360, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
          {calcs.map((c) => {
            const isCurrent = c.id === currentId;
            const overCap = c.over_capacity;
            return (
              <div key={c.id} style={{
                padding: 10, borderRadius: 8, border: `1px solid ${isCurrent ? C.lime : C.border}`,
                background: isCurrent ? C.limeDim : C.surface,
                display: "flex", alignItems: "center", gap: 10,
              }}>
                <button onClick={() => onLoad(c.id)} style={{
                  flex: 1, background: "none", border: "none", textAlign: "left",
                  cursor: "pointer", color: C.text, padding: 0,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {c.client_name || "Unnamed"}
                    {c.job_number ? ` · Job #${c.job_number}` : ""}
                    {!c.job_number && c.jobber_request_id ? " · Request" : ""}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                    {c.demand_amps}A on {c.service_size_amps}A
                    {overCap ? <span style={{ color: C.red, marginLeft: 6 }}>· OVER</span> : null}
                    {" · "}{formatDate(c.created_at)}
                    {c.created_by_email ? ` · ${c.created_by_email.split("@")[0]}` : ""}
                  </div>
                  {(c.job_title || c.request_title) && (
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {c.job_title || c.request_title}
                    </div>
                  )}
                </button>
                <button onClick={() => handleDelete(c.id)} disabled={deletingId === c.id} style={{
                  background: "none", border: "none", color: C.dim, cursor: "pointer",
                  fontSize: 16, padding: "4px 8px",
                }} title="Delete">
                  {deletingId === c.id ? "…" : "🗑"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Workspace ─────────────────────────────────────────────────────────────────
export default function LoadCalcWorkspace() {
  // calcKey forces CECLoadCalc to remount when reloading a saved calc.
  const [calcKey, setCalcKey] = useState(0);
  const [initialState, setInitialState] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [savedCalc, setSavedCalc] = useState(null);
  const emptyMeta = {
    client_name: "", job_address: "",
    jobber_client_id: "",
    jobber_job_id: "", job_number: "", job_title: "",
    jobber_request_id: "", request_title: "",
    notes: "",
  };
  const [meta, setMeta] = useState(emptyMeta);
  const [showSave, setShowSave] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  const handleStateChange = useCallback((snap) => setSnapshot(snap), []);

  const flashStatus = (msg) => {
    setStatusMsg(msg);
    setTimeout(() => setStatusMsg(""), 3500);
  };

  const startNew = () => {
    setSavedCalc(null);
    setInitialState(null);
    setMeta(emptyMeta);
    setCalcKey((k) => k + 1);
    setShowPast(false);
    setShowSave(false);
    flashStatus("Started a new blank calculation.");
  };

  const metaFromCalc = (c) => ({
    client_name:       c.client_name || "",
    job_address:       c.job_address || "",
    jobber_client_id:  c.jobber_client_id || "",
    jobber_job_id:     c.jobber_job_id || "",
    job_number:        c.job_number || "",
    job_title:         c.job_title || "",
    jobber_request_id: c.jobber_request_id || "",
    request_title:     c.request_title || "",
    notes:             c.notes || "",
  });

  // Carry-through: if a saved calc is linked to a request that has since been
  // converted to a job in Jobber, fold the new job_id back into the saved row.
  // Returns the updated calc (or the original if nothing changed). Best-effort:
  // network/API failures don't block the load.
  const resolveJobFromRequest = async (calc) => {
    if (!calc?.jobber_request_id || calc.jobber_job_id) return calc;
    try {
      const r = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "getRequestJobs", requestId: calc.jobber_request_id }),
      });
      const data = await r.json();
      const job = data?.ok ? (data.jobs || [])[0] : null;
      if (!job) return calc;
      const update = await apiFetch("/api/load-calc", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "save",
          id: calc.id,
          jobber_job_id: job.id,
          job_number: String(job.jobNumber || ""),
          job_title: job.title || calc.job_title || null,
          inputs: calc.inputs,
          result: calc.result,
        }),
      });
      const updated = await update.json();
      return updated?.ok ? updated.calc : calc;
    } catch {
      return calc;
    }
  };

  const loadCalc = async (id) => {
    try {
      const res = await apiFetch(`/api/load-calc?action=get&id=${encodeURIComponent(id)}`);
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Load failed");
      let c = data.calc;

      // Hydrate UI immediately, then carry-through resolve in the background.
      setSavedCalc(c);
      setInitialState(c.inputs);
      setMeta(metaFromCalc(c));
      setCalcKey((k) => k + 1);
      setShowPast(false);
      flashStatus(
        `Loaded: ${c.client_name || "calc"}${c.job_number ? ` · #${c.job_number}` : (c.jobber_request_id ? " · request" : "")}`,
      );

      // Auto-resolve request → job in case it converted since last save.
      const resolved = await resolveJobFromRequest(c);
      if (resolved && resolved !== c && resolved.jobber_job_id !== c.jobber_job_id) {
        setSavedCalc(resolved);
        setMeta(metaFromCalc(resolved));
        flashStatus(`Request has been converted — now linked to Job #${resolved.job_number}.`);
      }
    } catch (err) {
      flashStatus(`Couldn't load: ${err.message}`);
    }
  };

  const generatePdf = () => {
    if (!snapshot || !snapshot.result) {
      flashStatus("Enter the living area first to produce a result.");
      return;
    }
    const html = buildPrintHtml({ snapshot, meta });
    const win = window.open("", "_blank", "width=900,height=1100");
    if (!win) {
      flashStatus("Popup blocked — allow popups to generate the PDF.");
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  const onSaved = (calc, noteOutcome) => {
    setSavedCalc(calc);
    let msg = `Saved${calc?.id ? "" : " (no id returned)"}.`;
    if (noteOutcome?.ok) {
      msg += noteOutcome.edited
        ? " Updated the load calc note on the request in Jobber."
        : " Load calc pushed to the request's Jobber notes.";
    } else if (noteOutcome && !noteOutcome.ok) {
      msg += ` (Couldn't push to Jobber request notes: ${noteOutcome.error})`;
    }
    flashStatus(msg);
    setShowSave(false);
  };

  const onPushedToJobber = (noteId) => {
    if (savedCalc?.id && noteId) {
      // Persist the Jobber note id alongside the saved calc.
      apiFetch("/api/load-calc", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "save", id: savedCalc.id, jobber_note_id: noteId,
          inputs: savedCalc.inputs, result: savedCalc.result,
        }),
      }).catch(() => {});
    }
  };

  const hasResult = !!(snapshot && snapshot.result);

  return (
    <div>
      {/* Toolbar */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12,
      }}>
        <button onClick={() => { setShowPast((v) => !v); setShowSave(false); }} style={toolbarBtn(showPast)}>
          {showPast ? "▾" : "▸"} Past Calcs
        </button>
        <button onClick={() => { setShowSave((v) => !v); setShowPast(false); }}
          disabled={!hasResult} style={toolbarBtn(showSave, !hasResult)}>
          {savedCalc ? "Update Save" : "Save"}
        </button>
        <button onClick={generatePdf} disabled={!hasResult} style={toolbarBtn(false, !hasResult, C.lime)}>
          📄 PDF
        </button>
        <button onClick={startNew} style={toolbarBtn(false, false)}>
          + New
        </button>
        {savedCalc && (
          <div style={{
            marginLeft: "auto", padding: "6px 10px", borderRadius: 6,
            background: C.limeDim, border: `1px solid ${C.lime}`,
            fontSize: 11, color: C.lime, fontWeight: 700,
            display: "flex", alignItems: "center", gap: 6,
          }}>
            ● Saved
            {savedCalc.client_name ? ` · ${savedCalc.client_name}` : ""}
            {savedCalc.job_number
              ? ` · Job #${savedCalc.job_number}`
              : (savedCalc.jobber_request_id ? " · Request" : "")}
          </div>
        )}
      </div>

      {statusMsg && (
        <div style={{
          fontSize: 12, color: C.lime, padding: "6px 10px", marginBottom: 10,
          background: C.limeDim, border: `1px solid ${C.lime}`, borderRadius: 6,
        }}>{statusMsg}</div>
      )}

      {showPast && (
        <PastCalcsDrawer
          onLoad={loadCalc}
          onClose={() => setShowPast(false)}
          currentId={savedCalc?.id}
        />
      )}

      {showSave && hasResult && (
        <SavePanel
          snapshot={snapshot}
          savedCalc={savedCalc}
          meta={meta}
          setMeta={setMeta}
          onSaved={onSaved}
          onClose={() => setShowSave(false)}
          onPushedToJobber={onPushedToJobber}
        />
      )}

      {/* The calculator. `key` forces a remount when reloading a saved calc. */}
      <CECLoadCalc
        key={calcKey}
        initialState={initialState}
        onStateChange={handleStateChange}
      />
    </div>
  );
}

function toolbarBtn(active, disabled = false, accent = C.blue) {
  return {
    padding: "8px 14px",
    borderRadius: 8,
    border: `1px solid ${active ? accent : C.border}`,
    background: active ? `${accent}22` : C.surface,
    color: disabled ? C.dim : (active ? accent : C.text),
    fontSize: 12,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    fontFamily: "inherit",
  };
}
